import { FounderAskContextPaneView } from './FounderAskContextPanel'
import { FounderAskAuthor, FounderAskReply, FounderAskThread } from '../../lib/founderAsks'
import { CreatePanel } from '../shared/CreatePanel'

// Dev-only harness: renders the founder-ask context pane at 1, 2, 5, and 20
// participants with fixture data, for visual review of the scale behavior.
// Mounted at /test-pane in development builds only (see App.tsx).

const FIRST_NAMES = [
  'Maya', 'Jonas', 'Priya', 'Sam', 'Ingrid', 'Theo', 'Amara', 'Felix', 'Noor', 'Casper',
  'Lena', 'Ravi', 'Sofia', 'Emil', 'Hana', 'Otis', 'Freya', 'Dev', 'Clara', 'Marcus',
]

const founder = (id: string, first: string, last: string): FounderAskAuthor => ({
  id, first_name: first, last_name: last, username: first.toLowerCase(), avatar_url: null, is_founder: true,
})

const member = (i: number): FounderAskAuthor => ({
  id: `member-${i}`,
  first_name: FIRST_NAMES[i % FIRST_NAMES.length],
  last_name: `M${i}`,
  username: `member-${i}`,
  avatar_url: null,
})

const FOUNDERS = [founder('f-julia', 'Julia', 'Founder'), founder('f-liz', 'Elizabeth', 'Founder')]
const ASKER = member(0)

// Deterministic timestamps: participant i's replies land i minutes apart.
const at = (minutes: number) => new Date(Date.UTC(2026, 7, 1, 12, minutes)).toISOString()

function fixtureThread(participantCount: number, founderReplies: number): FounderAskThread {
  const replies: FounderAskReply[] = []

  for (let f = 0; f < founderReplies; f++) {
    replies.push({
      id: `fr-${f}`,
      ask_id: 'demo-ask',
      user_id: FOUNDERS[f].id,
      parent_reply_id: null,
      content: 'A founder reply.',
      created_at: at(5 + f),
      author_is_founder: true,
      user: FOUNDERS[f],
    })
  }

  // Participant 1 is the asker; give later participants more messages so the
  // count-desc sort is visible.
  for (let p = 0; p < participantCount; p++) {
    const u = member(p)
    const messages = 1 + (p % 4)
    for (let m = 0; m < messages; m++) {
      replies.push({
        id: `r-${p}-${m}`,
        ask_id: 'demo-ask',
        user_id: u.id,
        parent_reply_id: null,
        content: `Reply ${m + 1} from ${u.first_name}`,
        created_at: at(10 + p + m),
        author_is_founder: false,
        user: u,
      })
    }
  }

  return {
    ask: {
      id: 'demo-ask',
      user_id: ASKER.id,
      question: 'How did you decide which brands make the cut for the collective?',
      detail:
        'I keep going back and forth on whether certifications actually mean anything, or if you two look at something else entirely when you vet a brand. Would love the honest version.',
      directed_to: 'founders',
      reply_count: replies.length,
      last_reply_at: replies[replies.length - 1]?.created_at ?? null,
      first_answered_at: founderReplies > 0 ? at(5) : null,
      is_hidden: false,
      created_at: at(0),
      updated_at: at(0),
      author: ASKER,
      attachments: [],
      is_answered: founderReplies > 0,
    },
    replies,
  }
}

const CASES: { label: string; participants: number; founderReplies: number }[] = [
  { label: '1 participant', participants: 1, founderReplies: 1 },
  { label: '2 participants', participants: 2, founderReplies: 1 },
  { label: '5 participants', participants: 5, founderReplies: 2 },
  { label: '20 participants', participants: 20, founderReplies: 2 },
]

export function FounderAskPaneDemo() {
  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <h1 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-6">
        Founder-ask context pane — scale cases (fixed 720px frames)
      </h1>
      <div className="flex gap-6 items-start overflow-x-auto pb-4">
        {CASES.map(c => (
          <div key={c.label} className="flex-shrink-0">
            <p className="text-xs font-medium text-gray-600 mb-2">{c.label}</p>
            <div className="w-[400px] h-[720px] bg-white shadow-sm border border-gray-200 overflow-hidden">
              <FounderAskContextPaneView
                askThread={fixtureThread(c.participants, c.founderReplies)}
                founders={FOUNDERS}
                onFounderSelect={id => console.log('founder select →', id)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Create panel, mounted open with live previews (desktop variant) */}
      <h1 className="text-sm font-bold uppercase tracking-widest text-gray-500 mt-10 mb-4">
        Create panel — live previews
      </h1>
      <div className="relative w-[1180px] bg-white border border-gray-200" style={{ height: 8 }}>
        <CreatePanel open username="demo" onClose={() => {}} />
      </div>
    </div>
  )
}
